import config from '../config';
import selectedEnvironment from '../env';

const BASEAPI = config[selectedEnvironment].API_URL;

const LEDGER_API_URL = `${BASEAPI}/Ledgers`;

const getCurrentToken = async () => {
    const token = localStorage.getItem('token');
    return token ? JSON.parse(token) : null;
};

const LedgerReportsService = {
    // Document generation audit logs (bulk receipts / invoices / repair runs)
    getDocumentGenerationRuns: async (caseId, { fromDate, toDate, operationType } = {}) => {
        const token = await getCurrentToken();
        const params = new URLSearchParams({ caseId });
        if (fromDate) params.append('fromDate', fromDate);
        if (toDate) params.append('toDate', toDate);
        if (operationType) params.append('operationType', operationType);

        const response = await fetch(`${LEDGER_API_URL}/DocumentGenerationRuns?${params.toString()}`, {
            method: 'GET',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
        });

        if (!response.ok) {
            throw new Error('Failed to load document generation runs');
        }
        return response.json();
    },

    getDocumentGenerationRunItems: async (runId) => {
        const token = await getCurrentToken();
        const response = await fetch(
            `${LEDGER_API_URL}/DocumentGenerationRunItems?runId=${encodeURIComponent(runId)}`,
            {
                method: 'GET',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
            }
        );

        if (!response.ok) {
            throw new Error('Failed to load document generation run items');
        }
        return response.json();
    },

    // Single Client Reports - PDF
    downloadBalancesPaymentsReportPDF: async (caseId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadBalancesPaymentsReport`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    caseId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download balances payments report PDF');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadChargesReportPDF: async (caseId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadChargesReport`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    caseId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download charges report PDF');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadSummaryReportPDF: async (caseId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadSummaryReport`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    caseId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download summary report PDF');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    // Single Client Reports - Excel
    downloadBalancesPaymentsReportExcel: async (caseId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadBalancesPaymentsExcel`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    caseId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download balances payments report Excel');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadChargesReportExcel: async (caseId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadChargesExcel`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    caseId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download charges report Excel');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadSummaryReportExcel: async (caseId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadSummaryExcel`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    caseId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download summary report Excel');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    // Bulk Reports - PDF (for all clients by accountant)
    downloadBalancesPaymentsReportBulkPDF: async (accountantId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadBalancesPaymentsReportBulk`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountantId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download balances payments bulk report PDF');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadChargesReportBulkPDF: async (accountantId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadChargesReportBulk`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountantId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download charges bulk report PDF');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadSummaryReportBulkPDF: async (accountantId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadSummaryReportBulk`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountantId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download summary bulk report PDF');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    // Bulk Reports - Excel (for all clients by accountant)
    downloadBalancesPaymentsReportBulkExcel: async (accountantId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadBalancesPaymentsExcelBulk`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountantId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download balances payments bulk report Excel');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadChargesReportBulkExcel: async (accountantId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadChargesExcelBulk`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountantId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download charges bulk report Excel');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    downloadSummaryReportBulkExcel: async (accountantId, startDate, endDate) => {
        try {
            const token = await getCurrentToken();
            const response = await fetch(`${LEDGER_API_URL}/DownloadSummaryExcelBulk`, {
                method: 'POST',
                headers: {
                    Authorization: 'Bearer ' + token,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    accountantId,
                    startDate,
                    endDate
                }),
            });

            if (!response.ok) {
                throw new Error('Failed to download summary bulk report Excel');
            }
            return response.blob();
        } catch (error) {
            throw error;
        }
    },

    // Collection-page grid exports (WYSIWYG of the on-screen tables).
    // The payload carries the exact rows/labels/totals shown so the .xlsx mirrors the table.
    downloadMonthlyChargesTableExcel: async (payload) => {
        const token = await getCurrentToken();
        const response = await fetch(`${LEDGER_API_URL}/DownloadMonthlyChargesTableExcel`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error('Failed to download monthly charges Excel');
        }
        return response.blob();
    },

    downloadPaymentsTableExcel: async (payload) => {
        const token = await getCurrentToken();
        const response = await fetch(`${LEDGER_API_URL}/DownloadPaymentsTableExcel`, {
            method: 'POST',
            headers: {
                Authorization: 'Bearer ' + token,
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            throw new Error('Failed to download payments Excel');
        }
        return response.blob();
    },

    // Helper method to download file.
    // Returns:
    // - true  -> file was saved/download flow completed
    // - false -> user canceled the save dialog (only detectable with File System Access API)
    DownloadFile: async (blob, filename) => {
        if (typeof window === 'undefined') return false;

        // Modern browsers: can detect cancel reliably.
        if (typeof window.showSaveFilePicker === 'function') {
            try {
                const extension = filename?.includes('.')
                    ? `.${filename.split('.').pop().toLowerCase()}`
                    : '.txt';

                const handle = await window.showSaveFilePicker({
                    suggestedName: filename,
                    types: [
                        {
                            description: 'Download file',
                            accept: {
                                'application/octet-stream': [extension],
                            },
                        },
                    ],
                });

                const writable = await handle.createWritable();
                await writable.write(blob);
                await writable.close();

                return true;
            } catch (error) {
                if (error?.name === 'AbortError') {
                    return false;
                }
                throw error;
            }
        }

        // Fallback: classic browser download (cancel cannot be detected reliably here).
        try {
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            return true;
        } catch (error) {
            throw error;
        }
    },
    // Helper method to download file
    downloadFile: (blob, filename) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
    }
};

export default LedgerReportsService; 